/**
 * Auth Telemetry Module
 *
 * Provides hooks for tracking auth-related events.
 * Currently logs to console in development; can be wired
 * to an analytics endpoint in production.
 */

export type AuthEvent =
  | { name: 'login_success'; userId: number }
  | { name: 'login_failure'; error: string }
  | { name: 'signup_success'; userId: number }
  | { name: 'signup_failure'; error: string }
  | { name: 'logout' }
  | { name: 'token_refresh_success' }
  | { name: 'token_refresh_failure'; error: string }
  | { name: 'api_key_created'; scope?: string[] }
  | { name: 'api_key_revoked'; keyId: number }
  | { name: 'device_code_approved' }
  | { name: 'device_code_failed'; error: string }
  | { name: 'low_balance_shown'; balance: number }
  | { name: 'top_up_clicked'; credits: number }
  | { name: 'manage_billing_clicked' }
  | { name: 'portal_clicked' };

type TelemetryHandler = (event: AuthEvent, metadata?: Record<string, unknown>) => void;

const handlers: TelemetryHandler[] = [];

function defaultHandler(event: AuthEvent, metadata?: Record<string, unknown>): void {
  if (process.env.NODE_ENV === 'development') {
    console.log('[AuthTelemetry]', event.name, event, metadata);
  }
}

handlers.push(defaultHandler);

export function trackAuthEvent(event: AuthEvent, metadata?: Record<string, unknown>): void {
  for (const handler of handlers) {
    try {
      handler(event, metadata);
    } catch (err) {
      console.error('[AuthTelemetry] Handler error:', err);
    }
  }
}

export function registerTelemetryHandler(handler: TelemetryHandler): () => void {
  handlers.push(handler);
  return () => {
    const idx = handlers.indexOf(handler);
    if (idx !== -1) {
      handlers.splice(idx, 1);
    }
  };
}
